import { parseCookies, setCookie, getRequestHeader } from 'vinxi/http';
import { getRequestEvent } from 'solid-js/web';
import { langISO } from '@bz/i18n';
import jsCookies from 'js-cookie';

import { Setting, type SettingCookie, SETTING_COOKIE_KEYS } from './type';
import { createResolvedStore } from './globalStore';

import { VERSION_CLEARED, DEFAULT_LANG } from '~/constants';

/** 获取服务器端的 cookies */
const getSSRCookies = () => {
  'use server';
  const { nativeEvent, locals } = getRequestEvent()!;
  const cookies = parseCookies(nativeEvent);
  return locals[VERSION_CLEARED] ? {} : cookies;
};

/** 设置服务器端的 cookies */
const setSSRCookies = (key: keyof SettingCookie, value: unknown) => {
  'use server';
  const { nativeEvent } = getRequestEvent()!;
  return setCookie(nativeEvent, key, String(value), {
    expires: new Date(Date.now() + 3e10),
  });
};

/** 获取客户端的 cookies */
const getCSRCookies = () => {
  return jsCookies.get();
};

/** 设置客户端的 cookies */
const setCSRCookies = (key: keyof SettingCookie, value: unknown) => {
  return jsCookies.set(key, String(value), {
    expires: new Date(Date.now() + 3e10),
  });
};

/**
 * 同步设置 cookies
 * @param key
 * @param value
 */
const syncCookies = (data: Partial<SettingCookie>) => {
  for (const [key, value] of Object.entries(data)) {
    if (import.meta.env.SSR) {
      setSSRCookies(key as keyof SettingCookie, value);
    } else {
      setCSRCookies(key as keyof SettingCookie, value);
    }
  }
};

/** 服务端获取浏览器 Accept-Language */
const getSSRLang = () => {
  'use server';
  const { nativeEvent } = getRequestEvent()!;
  const raw = getRequestHeader(nativeEvent, 'accept-language');

  if (!raw || typeof raw !== 'string') return undefined;

  // zh-CN,zh;q=0.9,en;q=0.8
  return raw.split(',')[0]?.trim() ?? undefined;
};

/** 获取客户端设置的默认语言 */
const getCSRLang = () => {
  return navigator.language;
};

/** 获取默认语言 */
const getDefaultLang = () => {
  const lang = import.meta.env.SSR ? getSSRLang() : getCSRLang();
  if (!lang) return DEFAULT_LANG;

  const supported = Object.keys(langISO);
  const lower = lang.toLowerCase();

  /** 精确匹配 */
  const exact = supported.find((s) => s.toLowerCase() === lower);
  if (exact) return exact;

  /** 模糊匹配 */
  const fuzzy = supported.find((s) => s.toLowerCase().startsWith(lower));
  if (fuzzy) return fuzzy;

  /** 降级匹配 */
  return DEFAULT_LANG;
};

/**
 * 根据字符串类型推断并解析为对应的类型
 * @param str
 * @returns
 */
const inferAndParse = (str?: string) => {
  // 布尔
  if (str === 'true' || str === 'false') {
    return str === 'true';
  }
  // 数值
  const num = Number(str);
  if (str !== '' && !isNaN(num)) {
    return num;
  }
  // 默认字符串
  return str;
};

const [setting, mountSetting, setSetting] = createResolvedStore<SettingCookie>(() => {
  const cookies = import.meta.env.SSR ? getSSRCookies() : getCSRCookies();

  /**
   * @attention
   * 后续如果新增一个cookie，
   * 需要在此处对应添加cookie的默认值
   */
  const defaultSetting: SettingCookie = {
    lang: getDefaultLang(),
    theme: Setting.Theme.bakos,
    decimal: Setting.Decimal.european,
    currency: '',
    currencyView: '',
    balanceType: Setting.BalanceType.cash,
    marketMode: Setting.MarketMode.STANDARD,
  };

  const filtered = Object.fromEntries(
    SETTING_COOKIE_KEYS.map((key) => {
      let value = inferAndParse(cookies[key]);
      value === undefined && (value = defaultSetting[key]);
      return [key, value];
    })
  ) as SettingCookie;

  return filtered;
});

const useSetSetting = (
  /**
   * 是否直接同步设置
   * true: 跳过 setSetting，只设置 cookies
   */
  directSync: boolean = false
) => {
  const updatePreferenceAction = useAction(post.createUserSettingUpdatePreference);

  return (
    /** 需要设置的cookie数据 */
    data: Partial<SettingCookie>,
    /** 可选配置 */
    options?: {
      /** 是否同步到服务器 */
      syncToServer?: boolean;
      /** 回调函数 */
      syncSuccess?: VoidFunction;
    }
  ) => {
    syncCookies(data);
    !directSync && setSetting(data);

    const proFuncs: Promise<any>[] = [];
    if (options?.syncToServer) {
      const { lang, theme, marketMode, decimal } = data;
      const target = Object.fromEntries(
        [
          ['language', lang],
          ['displayMode', theme],
          ['marketMode', marketMode],
          ['decimalMode', decimal],
        ].filter(([_, value]) => value !== undefined)
      );

      proFuncs.push(
        updatePreferenceAction(target).then(() => {
          options?.syncSuccess?.();
        })
      );
    }

    return Promise.all(proFuncs);
  };
};

export { Setting, setting, mountSetting, setSetting, useSetSetting };
